import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import axios from '../../api/appPublicApi';
import { clearSmartOrderCart, setSmartOrderCart } from '../../Redux/SmartOrderCartSlice';
import SettingsView from './Components/SettingsView';
import SuggestedOrderTable from './Components/SuggestedOrderTable/SuggestedOrderTable';
import SuggestionsView from './Components/SuggestionsView';
import TabBar from './Components/TabBar';
import HelpModal from './Components/HelpModal';
import { resolveCustomerToken } from './utils/customerToken';

const SmartOrders = () => {
  const [view, setView] = useState('card');
  const [token, setToken] = useState('');
  const dispatch = useDispatch();
  const selectedRows = useSelector((state) => state.smartOrderCart.value);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [helpModalTarget, setHelpModalTarget] = useState(null);
  const [isSmartOrderTableLoaded, setIsSmartOrderTableLoaded] = useState(false);
  const [resumePromptOpen, setResumePromptOpen] = useState(false);
  const [resumeDraftOrder, setResumeDraftOrder] = useState(null);
  const [webOrderId, setWebOrderId] = useState(null);
  const [webOrderItemMap, setWebOrderItemMap] = useState({});
  const hasCheckedForDraftRef = useRef(false);
  const webOrderIdRef = useRef(null);
  const webOrderItemMapRef = useRef({});
  const selectedRowsRef = useRef([]);

  useEffect(() => {
    webOrderIdRef.current = webOrderId;
  }, [webOrderId]);

  useEffect(() => {
    webOrderItemMapRef.current = webOrderItemMap;
  }, [webOrderItemMap]);

  useEffect(() => {
    selectedRowsRef.current = selectedRows;
  }, [selectedRows]);

  const normalizeOrderItems = useCallback((items) => {
    const incomingItems = Array.isArray(items) ? items : [];
    const nextMap = {};
    const nextCartItems = incomingItems
      .map((item) => {
        const sourceReference = item?.source_reference;
        const itemNumber = sourceReference !== undefined && sourceReference !== null
          ? String(sourceReference)
          : '';
        if (!itemNumber) {
          return null;
        }

        const webOrderItemId = item?.web_order_item_id;
        if (webOrderItemId !== undefined && webOrderItemId !== null) {
          nextMap[itemNumber] = webOrderItemId;
        }

        return {
          item_number: itemNumber,
          suggested_qty: Number(item?.ordered_qty) || 1,
          cost: Number(item?.displayed_price) || 0,
          line_source: 'smart_order',
          web_order_item_id: webOrderItemId,
        };
      })
      .filter(Boolean);

    return { nextCartItems, nextMap };
  }, []);

  const syncMostRecentDraft = useCallback(
    async ({ hydrateRedux = false } = {}) => {
      if (!token) {
        return null;
      }

      try {
        const response = await axios.post('/api/app/suggestedOrders/cart/findMostRecentOrder', {
          customerNumber: token,
        });

        const draftDetails = response?.data?.details || null;

        if (!draftDetails?.web_order_id) {
          setWebOrderId(null);
          setWebOrderItemMap({});
          if (hydrateRedux) {
            dispatch(setSmartOrderCart([]));
          }
          return null;
        }

        const { nextCartItems, nextMap } = normalizeOrderItems(draftDetails.items);
        setWebOrderId(draftDetails.web_order_id);
        setWebOrderItemMap(nextMap);

        if (hydrateRedux) {
          dispatch(setSmartOrderCart(nextCartItems));
        }

        return {
          draftDetails,
          cartItems: nextCartItems,
          itemMap: nextMap,
        };
      } catch (error) {
        console.error('Error syncing most recent draft order:', error);
        return null;
      }
    },
    [dispatch, normalizeOrderItems, token],
  );

  const ensureDraftOrder = useCallback(
    async (item) => {
      const existingOrderId = webOrderIdRef.current;
      if (existingOrderId) {
        return { orderId: existingOrderId, wasCreated: false };
      }

      if (!token || !item?.item_number) {
        return { orderId: null, wasCreated: false };
      }

      const orderedQty = Math.max(1, Number(item.suggested_qty) || 1);
      const displayedPrice = Number(item.cost) || 0;

      try {
        const response = await axios.post('/api/app/suggestedOrders/cart/startNewOrder', {
          customerNumber: token,
          itemNumber: String(item.item_number),
          sourceReference: String(item.item_number),
          orderedQty,
          displayedPrice,
        });

        const createdOrderId = Number(response?.data?.webOrderId) || null;
        setWebOrderId(createdOrderId);
        await syncMostRecentDraft({ hydrateRedux: false });
        return { orderId: createdOrderId, wasCreated: true };
      } catch (error) {
        console.error('Error starting new draft order:', error);
        return { orderId: null, wasCreated: false };
      }
    },
    [syncMostRecentDraft, token],
  );

  const handleCartItemAdd = useCallback(
    async (item) => {
      if (!token || !item?.item_number) {
        return;
      }

      const itemNumber = String(item.item_number);
      if (webOrderItemMapRef.current[itemNumber]) {
        return;
      }

      const { orderId, wasCreated } = await ensureDraftOrder(item);
      if (!orderId) {
        return;
      }

      if (wasCreated) {
        return;
      }

      try {
        const response = await axios.post('/api/app/suggestedOrders/cart/addItem', {
          customerNumber: token,
          webOrderId: orderId,
          itemNumber,
          sourceReference: itemNumber,
          orderedQty: Math.max(1, Number(item.suggested_qty) || 1),
          displayedPrice: Number(item.cost) || 0,
        });

        const newWebOrderItemId = response?.data?.details?.insertId;
        if (newWebOrderItemId) {
          setWebOrderItemMap((prev) => ({
            ...prev,
            [itemNumber]: newWebOrderItemId,
          }));
        } else {
          await syncMostRecentDraft({ hydrateRedux: false });
        }
      } catch (error) {
        console.error('Error adding cart item to draft order:', error);
      }
    },
    [ensureDraftOrder, syncMostRecentDraft, token],
  );

  const handleCartItemEdit = useCallback(
    async (itemNumber, orderedQty) => {
      if (!token || !itemNumber) {
        return;
      }

      const webOrderItemId = webOrderItemMapRef.current[String(itemNumber)];
      if (!webOrderItemId) {
        return;
      }

      const normalizedQty = Number(orderedQty);
      if (!Number.isFinite(normalizedQty) || normalizedQty < 1) {
        return;
      }

      try {
        await axios.post('/api/app/suggestedOrders/cart/editItem', {
          customerNumber: token,
          webOrderItemId,
          orderedQty: normalizedQty,
        });
      } catch (error) {
        console.error('Error editing cart item in draft order:', error);
      }
    },
    [token],
  );

  const handleClearPersistedCart = useCallback(async () => {
    const currentWebOrderId = webOrderIdRef.current;
    if (!token || !currentWebOrderId) {
      setWebOrderId(null);
      setWebOrderItemMap({});
      return;
    }

    try {
      await axios.post('/api/app/suggestedOrders/cart/clear', {
        customerNumber: token,
        webOrderId: currentWebOrderId,
      });
    } catch (error) {
      console.error('Error clearing draft cart:', error);
    } finally {
      setWebOrderId(null);
      setWebOrderItemMap({});
    }
  }, [token]);

  const rebuildPersistedCartFromRows = useCallback(
    async (rows) => {
      const remaining = (rows || []).filter((item) => item?.item_number);

      await handleClearPersistedCart();

      if (!remaining.length) {
        return;
      }

      const [firstItem, ...otherItems] = remaining;
      const { orderId } = await ensureDraftOrder(firstItem);
      if (!orderId) {
        return;
      }

      for (const item of otherItems) {
        try {
          await axios.post('/api/app/suggestedOrders/cart/addItem', {
            customerNumber: token,
            webOrderId: orderId,
            itemNumber: String(item.item_number),
            sourceReference: String(item.item_number),
            orderedQty: Math.max(1, Number(item.suggested_qty) || 1),
            displayedPrice: Number(item.cost) || 0,
          });
        } catch (error) {
          console.error('Error rebuilding persisted cart item:', error);
        }
      }

      await syncMostRecentDraft({ hydrateRedux: false });
    },
    [ensureDraftOrder, handleClearPersistedCart, syncMostRecentDraft, token],
  );

  const handleCartItemRemove = useCallback(
    async (itemNumber) => {
      if (!itemNumber) {
        return;
      }

      const webOrderItemId = webOrderItemMapRef.current[String(itemNumber)];
      if (!webOrderItemId || !token) {
        return;
      }

      try {
        await axios.post('/api/app/suggestedOrders/cart/editItem', {
          customerNumber: token,
          webOrderItemId,
          orderedQty: 0,
        });

        setWebOrderItemMap((prev) => {
          const next = { ...prev };
          delete next[String(itemNumber)];
          return next;
        });
      } catch (error) {
        console.error('Unable to remove item via editItem; rebuilding persisted cart.', error);
        const remainingRows = selectedRowsRef.current.filter(
          (row) => String(row.item_number) !== String(itemNumber),
        );
        await rebuildPersistedCartFromRows(remainingRows);
      }
    },
    [rebuildPersistedCartFromRows, token],
  );

  const handleSmartOrderTableLoaded = useCallback(() => {
    setIsSmartOrderTableLoaded(true);
  }, []);

  useEffect(() => {
    if (!token || !isSmartOrderTableLoaded || hasCheckedForDraftRef.current) {
      return;
    }

    hasCheckedForDraftRef.current = true;

    syncMostRecentDraft({ hydrateRedux: false }).then((payload) => {
      if (!payload?.draftDetails?.web_order_id) {
        return;
      }

      if (!payload.cartItems?.length) {
        return;
      }

      setResumeDraftOrder(payload);
      setResumePromptOpen(true);
    });
  }, [isSmartOrderTableLoaded, syncMostRecentDraft, token]);

  const handleResumeDraftYes = useCallback(async () => {
    setResumePromptOpen(false);

    const payload = resumeDraftOrder;
    if (!payload?.draftDetails?.web_order_id) {
      return;
    }

    dispatch(setSmartOrderCart(payload.cartItems || []));
    setWebOrderId(payload.draftDetails.web_order_id);
    setWebOrderItemMap(payload.itemMap || {});
  }, [dispatch, resumeDraftOrder]);

  const handleResumeDraftNo = useCallback(async () => {
    setResumePromptOpen(false);

    const draftOrderId = resumeDraftOrder?.draftDetails?.web_order_id;
    if (token && draftOrderId) {
      try {
        await axios.post('/api/app/suggestedOrders/cart/clear', {
          customerNumber: token,
          webOrderId: draftOrderId,
        });
      } catch (error) {
        console.error('Error clearing prior draft order:', error);
      }
    }

    setWebOrderId(null);
    setWebOrderItemMap({});
    dispatch(clearSmartOrderCart());
  }, [dispatch, resumeDraftOrder, token]);

  useEffect(() => {
    setToken(resolveCustomerToken());
  }, []);

  useEffect(() => {
    hasCheckedForDraftRef.current = false;
    setIsSmartOrderTableLoaded(false);
    setResumePromptOpen(false);
    setResumeDraftOrder(null);
  }, [token]);

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const cleanupAfterOrder = useCallback(() => {
    console.log('SmartOrders: Clearing cart and handling post-order actions.');
    dispatch(clearSmartOrderCart());
    setWebOrderId(null);
    setWebOrderItemMap({});

    setSnackbarMessage('Order placed successfully!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  }, [dispatch]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleOpenHelpModal = useCallback((target = null) => {
    setHelpModalTarget(target);
    setIsHelpModalOpen(true);
  }, []);

  const handleCloseHelpModal = useCallback(() => {
    setIsHelpModalOpen(false);
    setHelpModalTarget(null);
  }, []);

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <TabBar
          view={view}
          handleViewChange={handleViewChange}
          handleOrderPlaced={cleanupAfterOrder}
          customerNumber={token}
          webOrderId={webOrderId}
          onOpenHelp={handleOpenHelpModal}
          onPersistRemoveItem={handleCartItemRemove}
          onPersistQuantityChange={handleCartItemEdit}
        />
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          width: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {view === 'card' && (
          <Box sx={{ flexGrow: 1, width: '100%', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <SuggestedOrderTable
              onOpenHelp={handleOpenHelpModal}
              onTableLoaded={handleSmartOrderTableLoaded}
              onPersistAddItem={handleCartItemAdd}
              onPersistEditItem={handleCartItemEdit}
              onPersistRemoveItem={handleCartItemRemove}
              onPersistClearCart={handleClearPersistedCart}
              onPersistRebuildCart={rebuildPersistedCartFromRows}
              webOrderId={webOrderId}
            />
          </Box>
        )}
        {view === 'settings' && (
          <Box sx={{ flexGrow: 1, width: '100%', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <SettingsView onOpenHelp={handleOpenHelpModal} />
          </Box>
        )}
        {view === 'suggestions' && (
          <Box sx={{ flexGrow: 1, width: '100%', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <SuggestionsView
              onOpenHelp={handleOpenHelpModal}
              onPersistAddItem={handleCartItemAdd}
              onPersistEditItem={handleCartItemEdit}
              onPersistRemoveItem={handleCartItemRemove}
              webOrderId={webOrderId}
            />
          </Box>
        )}
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <HelpModal open={isHelpModalOpen} onClose={handleCloseHelpModal} target={helpModalTarget} />
      <Dialog open={resumePromptOpen} onClose={handleResumeDraftNo} maxWidth='xs' fullWidth>
        <DialogTitle>Continue Previous Cart?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            You have a previous draft cart. Do you want to continue where you left off?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResumeDraftNo} color='inherit'>
            No, Start Fresh
          </Button>
          <Button onClick={handleResumeDraftYes} variant='contained'>
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SmartOrders;
